import { useEffect } from 'react'
import axios from 'axios'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { router } from './router'
import { useAuthStore } from '@/store/auth'
import { authApi } from '@/api/auth'
import Spinner from '@/components/common/Spinner'
import Toaster from '@/components/common/Toaster'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Данные считаем свежими 10 сек; после этого при возврате на вкладку
      // или переподключении сети они автоматически подтягиваются заново.
      staleTime: 10_000,
      refetchOnWindowFocus: true, // вернулись на вкладку → сразу свежие данные
      refetchOnReconnect: true,   // восстановилась сеть → обновляемся
    },
  },
})

/**
 * Тихое восстановление сессии при старте приложения.
 * Access-токен живёт только в памяти, поэтому при перезагрузке страницы
 * (F5, редеплой фронта) его нет. Пробуем получить новый через HttpOnly
 * refresh-cookie — если она валидна, пользователь остаётся в системе.
 */
function useSilentRefresh() {
  const { setToken, setUser, setBootstrapped } = useAuthStore()
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await axios.post(
          '/api/v1/auth/refresh',
          {},
          { withCredentials: true },
        )
        if (cancelled) return
        setToken(data.access_token)
        const { data: me } = await authApi.me()
        if (cancelled) return
        setUser(me)
      } catch {
        // Нет валидной сессии — остаёмся неавторизованными (уйдём на /login)
      } finally {
        if (!cancelled) setBootstrapped()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [setToken, setUser, setBootstrapped])
}

export default function App() {
  useSilentRefresh()
  const bootstrapping = useAuthStore((s) => s.bootstrapping)

  return (
    <QueryClientProvider client={queryClient}>
      {bootstrapping ? (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <Spinner size="lg" />
        </div>
      ) : (
        <RouterProvider router={router} />
      )}
      <Toaster />
    </QueryClientProvider>
  )
}
